import type { Block } from '@/api/courses';

import { tableHasGaps, type TableFillContent } from './tableFill';
import { openItemsPromptEmpty } from './openItems';

/**
 * Есть ли в задании содержание, то есть можно ли на него вообще ответить.
 *
 * ЗАЧЕМ. Конструктор создаёт заготовку задания сразу, а автор заполняет её потом
 * — и не всегда. В базе на 17.08.2026 таких пустых заготовок **95 штук**:
 * анаграмма без слова, выбор формы без вариантов, эссе без задания, пропуск без
 * предложения. Показывать в них нечего, ответить нельзя.
 *
 * Раньше раздел ждал ответа на КАЖДОЕ обязательное задание, включая пустые. Один
 * такой блок намертво запирал раздел, раздел — урок, а урок в последовательном
 * марафоне — все следующие уроки. Ученик проходил всё, что видел, и упирался
 * в стену без объяснения: заблокировано было 14 уроков в трёх марафонах.
 *
 * Теперь пустая заготовка просто не считается обязательной. Это не «прощение
 * недоделки», а защита ученика от чужой недоделки: он не должен застревать
 * из-за того, что автор не дописал задание.
 */
export function blockHasContent(block: Block): boolean {
  const c = (block.content ?? {}) as Record<string, unknown>;
  const str = (k: string) => String(c[k] ?? '').trim();
  const arr = (k: string) => (Array.isArray(c[k]) ? (c[k] as unknown[]) : []);

  switch (block.type) {
    case 'anagram':
    case 'word_form_select':
      return str('word') !== '';
    case 'word_input_gap':
    case 'word_drag_to_gap':
    case 'word_drag_to_image':
    case 'word_input_image':
    case 'word_form_image':
      return str('answer') !== '' || str('sentence') !== '';
    case 'essay':
      return str('prompt') !== '';
    case 'open_items':
      // «Ответ по пунктам» без единого пункта с текстом — заготовка (13.10.2026, progress 000027).
      return !openItemsPromptEmpty(c);
    case 'gap_fill':
      return str('template') !== '';
    case 'table_fill':
      // Таблица без единого пропуска — заготовка: ответить не на что (13.10.2026).
      return tableHasGaps(c as TableFillContent);
    case 'matching':
      return arr('pairs').length > 0 || arr('items').length > 0;
    case 'quiz_no_timer':
    case 'quiz_with_timer':
      // Редактор при первой правке переносит вопрос в `questions`; вопрос без текста
      // и без единого варианта с текстом — заготовка, а не задание (техдолг 179).
      return str('question') !== '' || arr('questions').some(quizQuestionHasContent);
    case 'true_false_ng':
      return arr('statements').some((s) => String((s as { text?: string })?.text ?? '').trim() !== '');
    case 'paragraph_order':
      // Конструктор хранит абзацы в `paragraphs` (техдолг 166); `items`/`sentences` — старые формы.
      return (
        arr('paragraphs').some((p) => String(p ?? '').trim() !== '') ||
        arr('items').length > 0 ||
        arr('sentences').length > 0
      );
    case 'sentence_order':
    case 'column_sort':
      return arr('items').length > 0 || arr('sentences').length > 0 || arr('columns').length > 0;
    default:
      // Незнакомый тип считаем полноценным: лучше попросить ответ лишний раз,
      // чем молча пропустить настоящее задание.
      return true;
  }
}

/** Вопрос теста с текстом или хотя бы одним вариантом с текстом (варианты — строки или `{text}`). */
function quizQuestionHasContent(q: unknown): boolean {
  const o = (q ?? {}) as { text?: unknown; question?: unknown; options?: unknown };
  if (String(o.text ?? o.question ?? '').trim() !== '') return true;
  const opts = Array.isArray(o.options) ? o.options : [];
  return opts.some((opt) => {
    const t = typeof opt === 'object' && opt !== null ? (opt as { text?: unknown }).text : opt;
    return String(t ?? '').trim() !== '';
  });
}
